import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button, IconComponent, Text, Spacer, PointerRight,
} from '@myinterview/component-library';
// eslint-disable-next-line import/no-extraneous-dependencies
import { IWidgetRef, Widget } from '@myinterview/widget-react';
// @ts-ignore
import platform from 'platform';
import {
  CandidateStatus,
  IJobResponse, IParsedInputField, IVideo,
} from '../interfaces/jobInterface';
import { ICandidateDetails, ICompleteCallbackData, IMessageType } from '../interfaces';
import { GAevent } from '../utils/googleAnalytics';
import { deleteSession, registerDateEvent } from '../services/sessionService';
import { completeCallback } from '../services/interviewService';
import { hideIntercom } from '../utils/utils';
import { isDev } from '../utils/env.utils';
import { getEventId } from '../services/eventService';

interface ITracking {
  reminder?: number;
  source?: number;
}
interface IInfo {
  files: string[];
  personalInfo: Record<string, string>;
}
interface IProps {
  btnDisabled: boolean;
  job: IJobResponse;
  candidate: ICandidateDetails;
  video?: IVideo;
  candidateID?: string;
  inputFieldsData?: IParsedInputField;
  status: CandidateStatus;
  handleTopTipsClicked: (e: React.MouseEvent<HTMLButtonElement>) => void;
  handleWidgetEvent: (type?: IMessageType) => void;
  handleStopVideo: () => void;
}

export const ButtonContainer = ({
  btnDisabled,
  job,
  candidate,
  video,
  candidateID,
  inputFieldsData,
  status,
  handleTopTipsClicked,
  handleWidgetEvent,
  handleStopVideo,
}: IProps) => {
  const { t } = useTranslation();
  const myinterviewButtonRef = useRef<IWidgetRef>(null);

  const consentText = () => (
    <Text className="dynamic-direction" size="XS-Regular" fontStyle="italic">
      <span>{t('consent.click')}</span>
      <a className="color--special" href={job.job.termsUrl || 'https://www.myinterview.com/terms/'} target="_blank" rel="noreferrer">{t('consent.terms')}</a>
      <span>{t('consent.and')}</span>
      <a className="color--special" href={job.job.privacyUrl || 'https://www.myinterview.com/privacy/'} target="_blank" rel="noreferrer">{t('consent.privacy')}</a>
    </Text>
  );

  const completeCallbackData: ICompleteCallbackData = {
    apiKey: job.job.apiKey || 'qDp2egprFa',
    title: job.job.title,
    candidate_id: job.candidate?.candidate_id || candidateID,
    inviteId: job.candidate?.candidate_id, // What is the difference? Do we need both?
    status: 'completed',
    interviewId: job.job.id, // id === job_id === jobID
  };

  const onCompleteCallback = async (
    // eslint-disable-next-line @typescript-eslint/no-shadow
    completeCallbackData: ICompleteCallbackData,
    videoId: string,
  ) => {
    let sourceEvent = 'widget_complete_viadirect';
    const tracking: ITracking = {};
    const url = window.location.search;
    if (url.includes('s=1&r=1')) {
      sourceEvent = 'widget_complete_viareminder';
      tracking.reminder = 1;
      tracking.source = 1;
    } else if (url.includes('s=2&r=1')) {
      sourceEvent = 'widget_complete_viareminder_sms';
      tracking.reminder = 1;
      tracking.source = 2;
    } else if (url.includes('s=1')) {
      sourceEvent = 'widget_complete_viaemail';
      tracking.source = 1;
    } else if (url.includes('s=2')) {
      sourceEvent = 'widget_complete_viasms';
      tracking.source = 2;
    }
    GAevent(sourceEvent);
    handleWidgetEvent();
    await registerDateEvent('widget_completed');
    deleteSession();
    const data = {
      ...completeCallbackData,
      tracking,
      videoId,
    };
    const response = await completeCallback(data);
    if (response.data?.redirectUrl) {
      window.location.href = response.data.redirectUrl;
    }
  };

  const { personalInfo, files } = useMemo(
    () => Object.entries(inputFieldsData || {})
      .reduce<IInfo>((acc, [key, { value, type }]) => {
      if ((type === 'document' || key === 'cvUrl') && value) {
        acc.files.push(value);
      } else if (value) {
        acc.personalInfo[key] = value;
      }
      return acc;
    }, { personalInfo: {}, files: [] }),
    [inputFieldsData],
  );

  const isResumed:boolean = useMemo(() => !!video?.videos.some((v) => v.uploaded), [video]);

  useEffect(() => {
    if (status === CandidateStatus.UPLOADING) {
      myinterviewButtonRef.current?.open();
    }
  }, [status]);

  return (
    <>
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions,
      jsx-a11y/click-events-have-key-events */}
      <div onClick={() => handleStopVideo()} className="dynamic-direction btn-wrapper">
        <Widget
          ref={myinterviewButtonRef}
          disabled={btnDisabled}
          buttonStyle={{ width: '100%' }}
          buttonText={(isResumed ? t('form.resumeInterview') : t('form.startInterview')).toUpperCase()}
          config={{
            onError: (data) => handleWidgetEvent(data?.messageType),
            auth: job.widgetAuthorizationSignature,
            event_id: getEventId(),
            useLightDom: true,
            onFinish: (data) => {
              onCompleteCallback(completeCallbackData, data.video_id).then(() => {});
            },
            minimizable: true,
            hideIntercom: hideIntercom(job.job.company_id),
            env: isDev ? 'staging' : 'production',
            practiceQuestions: job.job.practiceQuestions,
          }}
          video={{
            video_id: video?.video_id || candidate.video_id || '',
            personalInfo,
            files,
            videos: [],
            platform,
            referrer: window.location.href,
            language: job?.job?.language || 'en',
          }}
          candidate={{
            candidate_id: job?.candidate?.candidate_id || candidateID,
            username: job?.candidate?.username || `${candidate.firstName} ${candidate.lastName}`,
            email: job?.candidate?.email || candidate.email,
            enableNotifications: true,
            reapply: true,
          }}
          job={{
            jobID: job?.job.id,
          }}
        />
        <Button onClick={handleTopTipsClicked} className="background-color--white">
          <div className="btn-tooltips ignore-direction">
            <IconComponent size="23px" icon={<PointerRight />} />
            <Spacer x={0.5} />
            <Text size="L-Regular">{t('top_tips.top_tips')}</Text>
          </div>
        </Button>
      </div>
      <Spacer y={2} />
      {consentText()}
    </>
  );
};
